import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — sanft hochzählende Zahl, sobald sie in den Viewport kommt.
 *
 * Props:
 *  - value: string, z. B. "150+", "12 J.", "94%".
 *  - duration: Millisekunden (default 1400)
 *
 * Erkennt automatisch das numerische Präfix und animiert nur die Zahl.
 * Nicht-numerische Suffixe (+, %, J.) bleiben statisch am Ende.
 */
export default function CountUp({ value, duration = 1600, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const match = String(value).match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (!match) {
      setDisplay(String(value));
      return undefined;
    }
    const target = parseFloat(match[1]);
    const suffix = match[2] || "";
    const decimals = (match[1].split(".")[1] || "").length;

    // Respect reduced motion — show final value instantly
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(`${target.toFixed(decimals)}${suffix}`);
      return undefined;
    }

    let raf;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        // easeOutExpo
        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        const current = target * eased;
        setDisplay(`${current.toFixed(decimals)}${suffix}`);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className} data-testid="count-up">
      {display}
    </span>
  );
}
